import { ColDef, ICellRendererParams } from 'ag-grid-community';

import RenderHTML from '@/components/RenderHTML';
import TreatmentGroupActions from '@/components/TreatmentGroupActions';
import type { TreatmentGroupActionHandlers } from '@/components/TreatmentGroupActions/types';
import { DAY_LABELS } from '@/constants/dayOfWeek';
import { TreatmentGroup } from '@/types/treatmentGroup';
import { Pill, Tooltip } from '@/ui';
import { daysUntil, formatOptionalCalendarDate, formatTimeOfDay } from '@/utils/dates';
import { formatCounselors, hasGroupEnded } from '@/utils/treatmentGroupSessions';

// A date that's still in the future hasn't taken effect yet -- flag it rather
// than presenting it as settled historical fact. The `T00:00:00` pins the
// YYYY-MM-DD to local midnight; without it `daysUntil`'s `new Date(str)` treats
// it as UTC and can flip the calendar day for viewers in negative-offset zones.
const isFutureDate = (dateStr: string) => daysUntil(`${dateStr}T00:00:00`) > 0;

const statusCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const group = params.data;
  if (!group) return '';
  return (
    <div className="flex flex-wrap items-center gap-1">
      {group.is_draft && (
        <Pill size="sm" variant="subtle" color="warning" className="inline leading-none">
          Draft
        </Pill>
      )}
      {group.end_date &&
        (hasGroupEnded(group) ? (
          <Pill size="sm" variant="subtle" color="neutral" className="inline leading-none">
            Ended
          </Pill>
        ) : (
          <Pill size="sm" variant="subtle" color="warning" className="inline leading-none">
            Ending
          </Pill>
        ))}
    </div>
  );
};

const appointmentTypeCellRenderer = (params: ICellRendererParams<TreatmentGroup>) =>
  params.data?.appointment_type_name ?? '';

const eligibilityCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const summary = params.data?.eligibility_summary;
  if (!summary) return '';
  // eligibility_summary is HTML (nested <ul>/<li> bullets) -- parse it rather
  // than dumping the raw markup as text.
  return <RenderHTML html={summary} className="prose prose-sm dark:prose-invert" />;
};

const scheduleCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const group = params.data;
  if (!group) return '';
  return (
    <div className="flex flex-col gap-1 py-1">
      <div className="flex flex-wrap items-center gap-1">
        {group.current_sessions.map((session) => (
          <Tooltip
            key={session.id}
            content="Starting in the future"
            disabled={!isFutureDate(session.effective_from)}>
            <Pill
              size="sm"
              variant="outline"
              color={isFutureDate(session.effective_from) ? 'warning' : 'neutral'}
              className="inline leading-none">
              {DAY_LABELS[session.day_of_week]} · {formatTimeOfDay(session.start_time)}
            </Pill>
          </Tooltip>
        ))}
      </div>
      <span className="text-xs text-gray-500">{group.timezone}</span>
    </div>
  );
};

const membersCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const group = params.data;
  if (!group) return '';
  const full = group.active_member_count >= group.capacity;
  return (
    <div className="flex items-center gap-2">
      <span className={full ? 'text-red-600 font-medium' : ''}>
        {group.active_member_count}/{group.capacity}
      </span>
      {full && (
        <Pill size="sm" variant="subtle" color="destructive" className="inline leading-none">
          Full
        </Pill>
      )}
    </div>
  );
};

const acceptingNewPatientsCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  if (!params.data) return '';
  const accepting = params.data.accepting_new_patients;
  return (
    <Pill
      size="sm"
      variant="subtle"
      color={accepting ? 'primary' : 'neutral'}
      className="inline leading-none">
      {accepting ? 'Yes' : 'No'}
    </Pill>
  );
};

const startDateCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const startDate = params.data?.start_date;
  if (!startDate) return formatOptionalCalendarDate(startDate);
  if (!isFutureDate(startDate)) return formatOptionalCalendarDate(startDate);
  return (
    <Pill size="sm" variant="subtle" color="warning" className="inline leading-none">
      Starts {formatOptionalCalendarDate(startDate)}
    </Pill>
  );
};

const endDateCellRenderer = (params: ICellRendererParams<TreatmentGroup>) => {
  const group = params.data;
  if (!group?.end_date || hasGroupEnded(group)) return formatOptionalCalendarDate(group?.end_date);
  return (
    <Pill size="sm" variant="subtle" color="warning" className="inline leading-none">
      Ends {formatOptionalCalendarDate(group.end_date)}
    </Pill>
  );
};

const actionsCellRenderer = (handlers: TreatmentGroupActionHandlers) =>
  function ActionsCell(params: ICellRendererParams<TreatmentGroup>) {
    const group = params.data;
    if (!group) return '';
    return <TreatmentGroupActions {...handlers} group={group} variant="compact" />;
  };

export const createColumnDefs = (
  handlers: TreatmentGroupActionHandlers
): ColDef<TreatmentGroup>[] => [
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    filter: false,
    flex: 2
  },
  {
    colId: 'status',
    headerName: 'Status',
    sortable: false,
    filter: false,
    flex: 1,
    // Inline so it beats AG Grid's runtime-injected `.ag-cell { display: inline-block }`.
    cellStyle: { display: 'flex', alignItems: 'center' },
    cellRenderer: statusCellRenderer
  },
  {
    field: 'appointment_type_name',
    headerName: 'Appointment type',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: appointmentTypeCellRenderer
  },
  {
    colId: 'counselor',
    headerName: 'Counselor',
    sortable: false,
    filter: false,
    flex: 1,
    valueGetter: (params) => (params.data ? formatCounselors(params.data.current_sessions) : '')
  },
  {
    colId: 'schedule',
    headerName: 'Sessions',
    sortable: false,
    filter: false,
    flex: 2,
    autoHeight: true,
    cellRenderer: scheduleCellRenderer
  },
  {
    colId: 'members',
    headerName: 'Capacity',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: membersCellRenderer
  },
  {
    field: 'accepting_new_patients',
    headerName: 'Accepting new patients',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: acceptingNewPatientsCellRenderer
  },
  {
    field: 'start_date',
    headerName: 'Effective date',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: startDateCellRenderer
  },
  {
    field: 'end_date',
    headerName: 'End date',
    sortable: true,
    filter: false,
    flex: 1,
    cellRenderer: endDateCellRenderer
  },
  {
    field: 'eligibility_summary',
    headerName: 'Eligibility',
    sortable: false,
    filter: false,
    flex: 2,
    autoHeight: true,
    wrapText: true,
    cellRenderer: eligibilityCellRenderer
  },
  {
    colId: 'actions',
    headerName: '',
    sortable: false,
    filter: false,
    resizable: false,
    flex: 0,
    // Three icon-md buttons (32px, "compact" variant: View, Edit, kebab)
    // plus two gap-3 (12px) gaps come to exactly 120px with nothing left
    // over for the grid's own cell padding, so at 120px the kebab always
    // renders clipped past the column edge.
    width: 160,
    cellRenderer: actionsCellRenderer(handlers)
  }
];

export const COLUMN_ORDERING_MAP: Record<string, string> = {
  name: 'name',
  appointment_type_name: 'appointment_type__name',
  members: 'active_member_count',
  accepting_new_patients: 'accepting_new_patients',
  start_date: 'start_date',
  end_date: 'end_date'
};
